import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { PageTitle, useSession } from '@cordata/buffet';
import { format, differenceInYears } from 'date-fns';
import lowerCase from 'lodash/lowerCase';
import Helmet from 'react-helmet';

import PatientsBannerModal from '~/components/Patient/Identity/Banner/Modal';

import log from '~/utils/event/log';
import { UncontrolledTable } from '~/components';

const filterMethod = (filter, row) =>
  lowerCase(row[filter.id]).includes(lowerCase(filter.value));

const ResultsTable = ({ incomingParams, ...props }) => {
  return (
    <UncontrolledTable
      defaultPageSize={50}
      name={`identity_search_results_go_here ${props}`}
      endpoint={{
        url: 'data/public/identity_search',
        params: incomingParams,
      }}
      columns={[
        {
          id: 'displayName',
          Header: 'Name',
          accessor: i => `${i.attributes.lastName}, ${i.attributes.firstName}`,
        },
        {
          id: 'birthDate',
          Header: 'DOB (Age)',
          accessor: 'attributes.birthDate',
          Cell: x => <div>{format(x.original.attributes.birthDate, 'MM/DD/YYYY')} ({differenceInYears(new Date(), x.original.attributes.birthDate)} years)</div>,
          filterMethod: (filter, row) =>
            lowerCase(row._original.attributes.birthDate).includes(lowerCase(filter.value)) ||
            String(differenceInYears(new Date(), row._original.attributes.birthDate))
              .includes(lowerCase(filter.value)),
        }, {
          id: 'agencyCounty',
          Header: 'PCSA: County Name',
          accessor: 'attributes.agencyCounty',
          Cell: x => <div>{x.original.attributes.agencyCounty}</div>,
          filterMethod,
        }, {
          id: 'sacwisPersonId',
          Header: 'SACWIS Person ID',
          accessor: 'attributes.sacwisPersonId',
          Cell: x => <div>{x.original.attributes.sacwisPersonId}</div>,
          filterMethod,
        }, {
          id: 'caseId',
          Header: 'SACWIS Case ID',
          accessor: 'attributes.caseId',
          Cell: x => <div>{x.original.attributes.caseId.join(', ')}</div>,
          filterMethod,
        }, {
          id: 'providerName',
          Header: 'Placement Provider',
          accessor: 'attributes.providerName',
          Cell: x => <div>{x.original.attributes.providerName}</div>,
          filterMethod,
        }, {
          id: 'placementType',
          Header: 'Placement Type',
          accessor: 'attributes.placementType',
          Cell: x => <div>{x.original.attributes.placementType}</div>,
          filterMethod,
        }]}
      onClick={(row) => props.setId(row.attributes.patientUid)}
    />
  );
};

ResultsTable.propTypes = {
  incomingParams: PropTypes.shape(),
  setId: PropTypes.func.isRequired,
};

ResultsTable.defaultProps = {
  incomingParams: {},
};

const PatientsIndexIdentity = ({ searchParams, searchSubmitted }) => {
  const [id, setId] = useState();
  const [isBannerModalOpen, setIsBannerModalOpen] = useState(false);
  const { current } = useSession();

  const toggleBannerModal = () => {
    setId(undefined);
    setIsBannerModalOpen(!isBannerModalOpen);
  };

  useEffect(() => {
    log.create({ action: 'Search' });

    return () => {
      setIsBannerModalOpen(false);
      setId(undefined);
    };
  }, []);

  useEffect(() => {
    setIsBannerModalOpen(!!id);

    return () => setIsBannerModalOpen(false);
  }, [id]);

  const searchFormat = (params) => {
    const objectTranslationKeys = {
      agencyCounty: 'agencyCounty',
    };
    const formattedParams = {};

    Object.keys(params).forEach((key) => {
      let val = params[key];
      if (typeof val === 'string') {
        val = val.trim();
      }
      if (val !== null && val !== undefined && val !== '') {
        formattedParams[key] = val;
        if (key in objectTranslationKeys) {
          formattedParams[key] = val.attributes[objectTranslationKeys[key]];
        }
      }
    });

    return formattedParams;
  };

  return searchSubmitted
    ? (
      <>
        <PageTitle>Search Results</PageTitle>

        <ResultsTable
          incomingParams={searchFormat(searchParams)}
          setId={setId}
        />

        <PatientsBannerModal
          isOpen={isBannerModalOpen}
          toggleIsOpen={toggleBannerModal}
          patient={id}
        />
      </>
    ) : (

      <>
        <PageTitle>
          Welcome, {current?.attributes.displayName ||
            current?.attributes.userName}
        </PageTitle>

        <Helmet title={`Welcome, ${current?.attributes.displayName || current?.attributes.userName}`} />
        <>
          <h4 className="py-3">
            Cordata IDENTITY improves the coordination of services for children in foster care by{' '}
            aggregating health, legal, and custody information into a single database. Data is{' '}
            extracted from electronic health record and child welfare systems on a nightly basis.
          </h4>

          <h4>
            To find a child, enter information in the search criteria to the left{' '}
            and hit the blue Search button. Your view of the child’s data will be{' '}
            aligned with your designated role in the system.
          </h4>
        </>
      </>
    );
};

PatientsIndexIdentity.propTypes = {
  searchParams: PropTypes.shape(),
  searchSubmitted: PropTypes.bool,
};

PatientsIndexIdentity.defaultProps = {
  searchParams: () => {},
  searchSubmitted: false,
};

export default PatientsIndexIdentity;
